import { apiClient, apiClientBlob } from '@/lib/api-client';
import { forgetClosedRegisterSession, getActiveRegisterSessionId } from '@/stores/use-register-store';
import type { Bill, BillItem, BillPayment, BillPaymentMethod } from '@bbpos/types';

// ─── Request / Response types ────────────────────────────────────────────────

export interface CreateBillItemDto {
  product_id: string;
  quantity: number;
  /** Per-line discount in paise (must not exceed line subtotal). Server snapshots
   *  price from the catalog, so this flat amount is the only discount it reads on
   *  POST /bills — discount_type/value are NOT accepted on the online path. */
  discount?: number;
  /** The catalogue price the till showed when the line was added. The server
   *  still prices the bill; a mismatch comes back as 409 PRICE_CHANGED. */
  expected_unit_price?: number;
  /** Per-unit add-on (modifier) surcharge in paise, on top of the catalogue price. */
  modifier_surcharge?: number;
}

interface CreateBillDto {
  location_id?: string;
  items: CreateBillItemDto[];
  customer_id?: string;
  notes?: string;
  /** T12: the register session on this device; added from the register store when omitted. */
  register_session_id?: string;
  /** gst-parked 120: the cart's client id — an offline park uploads once, however often it is retried. */
  client_id?: string;
}

/** gst-parked 120: one row of GET /bills/parked — a parked bill of the location. */
export interface ServerParkedBill {
  id: string;
  bill_number: string;
  location_id: string;
  park_reference: string | null;
  /** BIGINT minor units — may arrive as a string. */
  grand_total: number | string;
  customer_name: string | null;
  parked_at: string;
  parked_by: string | null;
  parked_by_name: string | null;
  /** The short label the parking till sent (the items, or the customer). */
  note: string | null;
  item_count: number;
  client_id: string | null;
}

/** gst-parked 120: what POST /bills/:id/park answers. */
export interface ParkBillResult {
  id: string;
  bill_number: string;
  status: string;
  location_id: string;
  park_reference: string;
  note: string | null;
}

/** T12: the device's open register session, when the body does not name one. */
function withRegisterSession<T extends { location_id?: string; register_session_id?: string }>(dto: T): T {
  if (dto.register_session_id) return dto;
  const sessionId = getActiveRegisterSessionId(dto.location_id);
  return sessionId ? { ...dto, register_session_id: sessionId } : dto;
}

/**
 * Mirror of services/api/src/billing/dto/billing.dto.ts:UpdateBillDto.
 * PATCH /bills/:id is a line DIFF, not a replacement: `update_items` edits a
 * line in place (keeps its price snapshot), `add_items` are priced from the
 * catalogue, and totals are recomputed from the lines — which also wipes any
 * bill-level discount (re-apply it afterwards).
 */
export interface UpdateDraftBillDto {
  update_items?: { item_id: string; quantity?: number; discount?: number }[];
  add_items?: { product_id: string; quantity: number; discount?: number }[];
  remove_item_ids?: string[];
  customer_name?: string;
  customer_phone?: string;
  notes?: string;
}

interface CompleteBillDto {
  /** The bill's settlement method. Wider than a tender line: 'SPLIT' is valid
   *  here (the breakdown then lives in `payments`) but never on a line. */
  payment_method: BillPaymentMethod | 'SPLIT';
  amount_paid: number;
  /** Split tender lines. The server DTO names this field `method`, not
   *  `payment_method` — see SplitPaymentDto. Repeated methods are allowed. */
  payments?: { method: BillPaymentMethod; amount: number; credit_note_code?: string }[];
  customer_id?: string;
  rounding_adjustment?: number;
  /** Serial numbers per line — required for SERIAL-tracked products, one per unit. */
  serials?: { bill_item_id: string; serial_numbers: string[] }[];
  /** Bearer credit-note redemption — the code printed on the note. */
  credit_note_code?: string;
  /** Account Credit (T08): the customer's linked credit account, for a CREDIT tender or split leg. */
  credit_account_id?: string;
  /** T08: manager approval token for a sale on account over the credit limit. */
  approval_token?: string;
  /** T08: over the limit, answer 409 APPROVAL_REQUIRED so the till can ask for a manager PIN. */
  on_device_approval?: boolean;
  /** T10: the open quote this sale bills — the server closes and links it, and deducts its down payment. */
  quotation_id?: string;
  /** QA IS4-004: expired-batch units the cashier accepted — recorded in the audit log only. */
  expired_batch_accepted?: { product_id: string; batch_number: string; expiry_date: string; quantity: number }[];
  /** gst-parked D7: the cashier's IGST switch (INTER = IGST, INTRA = CGST + SGST); omit to let the server decide. */
  supply_override?: 'INTRA' | 'INTER';
  /** gst-parked D7: a manager's TAX_SWITCH approval for a cashier's switch. */
  supply_override_approval_token?: string;
}

// ─── Credit-note lookup ──────────────────────────────────────────────────────

export interface CreditNoteLookupResponse {
  code: string;
  amount_available: number;
  expires_at: string;
  customer_id: string | null;
  source_bill_id: string | null;
}

/**
 * Bill-level discount payload sent to POST /bills/:id/discount.
 *
 * Mirror of services/api/src/billing/dto/billing.dto.ts:ApplyDiscountDto.
 * IMPORTANT semantics:
 *   - `type` + `value` (not discount_type / discount_value — the previous
 *     shape was wrong and would have been rejected by class-validator).
 *   - For `PERCENTAGE`, `value` is in **basis points** (1000 = 10%). The
 *     modal collects whole percent; the caller must multiply by 100 before
 *     sending.
 *   - For `FLAT`, `value` is in paise.
 *   - `reason` is mandatory on the backend for audit.
 */
interface ApplyDiscountDto {
  type: 'PERCENTAGE' | 'FLAT';
  value: number;
  reason: string;
  voucher_code?: string;
}

interface ReturnItem {
  item_id: string;
  quantity: number;
}

interface ProcessReturnDto {
  /** Legacy whole-line form. Prefer `items` so partial quantities are possible. */
  item_ids?: string[];
  /** Per-line unit counts, e.g. returning 3 of 5 units. */
  items?: ReturnItem[];
  return_mode: string;
  reason: string;
  reason_notes?: string;
  /** T16: single-use token from a manager's on-device PIN approval. */
  approval_token?: string;
  /** T16: above the approval limit, answer APPROVAL_REQUIRED instead of asking a manager remotely. */
  on_device_approval?: boolean;
}

interface BillListFilters {
  status?: string;
  location_id?: string;
  start_date?: string;
  end_date?: string;
  search?: string;
  payment_method?: string;
  /** T27 search facets — each its own condition, ANDed. */
  customer_name?: string;
  customer_phone?: string;
  bill_number?: string;
  page?: number;
  limit?: number;
}

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  /** T27: over every bill the filters match, not the page — the export's sum (minor units). */
  summary?: { grand_total: number };
}

/** T03 — the status a return shows in POS. */
export type BillReturnStatus = 'DRAFT' | 'WAITING_APPROVAL' | 'COMPLETED' | 'REJECTED' | 'CANCELLED';

/** T03 — a return record: its RINV number stays the same from request to credit note. */
export interface BillReturn {
  id: string;
  return_number: string;
  status: BillReturnStatus;
  bill_id: string;
  location_id: string | null;
  approval_request_id: string | null;
  credit_note_bill_id: string | null;
  amount: number | null;
  return_mode: string | null;
  reason: string | null;
  review_note: string | null;
  execution_error: string | null;
  requested_by?: string | null;
  created_at: string;
  updated_at?: string;
  /** Returns list only. */
  bill_number?: string;
  customer_name?: string | null;
  credit_note_total?: number | null;
  requested_by_name?: string | null;
}

interface ReturnListFilters {
  status?: BillReturnStatus;
  location_id?: string;
  page?: number;
  limit?: number;
}

export interface BillWithItems extends Bill {
  items: Array<BillItem & {
    /** Units already returned on credit notes (GET /bills/:id). */
    returned_qty?: number;
    /** Units held by return requests still awaiting a manager (T02). */
    pending_return_qty?: number;
  }>;
  payments: BillPayment[];
  /** Set when a bearer credit note worth more than the bill was tendered:
   *  the surplus comes back as this new note, which the cashier hands over. */
  credit_note_remainder?: { redemption_code: string; amount: number; expires_at: string };
  /** T03 — returns against this bill, oldest first (GET /bills/:id). */
  returns?: BillReturn[];
  /** T04 — on a credit note: the store-credit code it issued (null for a cash refund). */
  redemption_code?: string | null;
  /** T04 — on a credit note with no code: the signed link its QR opens. */
  credit_note_verify_url?: string | null;
  /** T04 — on a credit note: the number of the bill it returns against. */
  original_bill_number?: string | null;
}

/**
 * POST /bills/:id/return either processes the return at once (credit note) or,
 * when the refund exceeds the tenant's approval threshold (FR-BILL-026), parks
 * it as an approval request and leaves the original bill COMPLETED.
 */
export type ProcessReturnResult =
  | { request_id: string; status: 'PENDING_APPROVAL'; return_id?: string; return_number?: string }
  | {
      /** CU2-003: points_back / points_back_value / money_refund are set when points came back. */
      credit_note: BillWithItems & { points_back?: number; points_back_value?: number; money_refund?: number };
      items_returned?: number;
      return?: { id: string; return_number: string; status: BillReturnStatus };
    };

// ─── Service ─────────────────────────────────────────────────────────────────

export const billingService = {
  /** Queue the invoice for delivery (POST /communications/send-invoice → 202). */
  sendInvoice(billId: string, channel: 'WHATSAPP' | 'EMAIL', recipient: string) {
    return apiClient<{ id: string; status: string }>('/communications/send-invoice', {
      method: 'POST',
      body: { bill_id: billId, channel, recipient },
    });
  },

  /**
   * What the live promotional rules take off each cart line (0 = none), in order.
   * `customerId` lets customer-segment rules match the attached customer.
   */
  previewPromotions(
    items: { product_id: string; quantity: number; unit_price: number; discount?: number }[],
    customerId?: string,
  ) {
    return apiClient<{ lines: { product_id: string; discount: number }[] }>('/bills/promotions/preview', {
      method: 'POST',
      body: customerId ? { items, customer_id: customerId } : { items },
    });
  },

  /** `idempotencyKey` (gst-parked 120): a resent create is replayed, not repeated. */
  async createBill(dto: CreateBillDto, idempotencyKey?: string) {
    try {
      return await apiClient<BillWithItems>('/bills', {
        method: 'POST',
        body: withRegisterSession(dto),
        ...(idempotencyKey ? { headers: { 'X-Idempotency-Key': idempotencyKey } } : {}),
      });
    } catch (err) {
      // Duck-typed: ApiError carries the envelope's code (tests often mock the client module).
      if ((err as { code?: string } | null)?.code === 'REGISTER_SESSION_NOT_OPEN') forgetClosedRegisterSession();
      throw err;
    }
  },

  completeBill(billId: string, dto: CompleteBillDto) {
    return apiClient<BillWithItems>(`/bills/${billId}/complete`, {
      method: 'POST',
      body: dto,
    });
  },

  /** Look up a bearer credit-note by its 6-char code. Throws on
   *  invalid / used / expired. The code is then sent on the subsequent
   *  {@link completeBill} call to actually redeem. */
  lookupCreditNote(code: string) {
    return apiClient<CreditNoteLookupResponse>(
      `/credit-notes/lookup?code=${encodeURIComponent(code)}`,
    );
  },

  /** gst-parked 120: park a DRAFT on the server; `note` labels it in every till's list (≤ 100 chars). */
  parkBill(billId: string, note?: string) {
    const label = note?.trim().slice(0, 100);
    return apiClient<ParkBillResult>(`/bills/${billId}/park`, {
      method: 'POST',
      body: label ? { note: label } : {},
    });
  },

  /** PARKED → DRAFT, claiming the draft lock. force: MANAGER+ takes over a bill open on another till. */
  resumeBill(billId: string, force = false) {
    return apiClient<BillWithItems>(`/bills/${billId}/resume${force ? '?force=true' : ''}`, {
      method: 'POST',
    });
  },

  /** Claim the draft-edit lock before hydrating a server draft. force: MANAGER+ takeover. */
  acquireBillLock(billId: string, force = false) {
    return apiClient<{ locked: boolean }>(`/bills/${billId}/lock${force ? '?force=true' : ''}`, {
      method: 'POST',
    });
  },

  /** Best-effort release of the draft-edit lock (claims never expire — the holding till releases). */
  releaseBillLock(billId: string) {
    return apiClient<{ released: boolean }>(`/bills/${billId}/release-lock`, {
      method: 'POST',
    });
  },

  voidBill(billId: string, reason?: string) {
    return apiClient<Bill>(`/bills/${billId}/void`, {
      method: 'PATCH',
      ...(reason?.trim() ? { body: { reason: reason.trim() } } : {}),
    });
  },

  deleteBill(billId: string) {
    return apiClient<void>(`/bills/${billId}`, {
      method: 'DELETE',
    });
  },

  applyDiscount(billId: string, dto: ApplyDiscountDto) {
    return apiClient<BillWithItems>(`/bills/${billId}/discount`, {
      method: 'POST',
      body: dto,
    });
  },

  /** Remove the bill-level discount from a DRAFT; the server recomputes totals. */
  clearDiscount(billId: string) {
    return apiClient<Bill>(`/bills/${billId}/discount`, {
      method: 'DELETE',
    });
  },

  /**
   * FR-BILL-050: Request approval for a discount exceeding the caller's
   * role cap. Returns the approval request ID and PENDING_DISCOUNT_APPROVAL status.
   */
  requestDiscountApproval(billId: string, dto: Omit<ApplyDiscountDto, 'voucher_code'>) {
    return apiClient<{ request_id: string; status: string }>(`/bills/${billId}/discount-approval`, {
      method: 'POST',
      body: dto,
    });
  },

  getBill(billId: string) {
    return apiClient<BillWithItems>(`/bills/${billId}`);
  },

  listBills(filters: BillListFilters = {}) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null) {
        params.set(key, String(value));
      }
    }
    const qs = params.toString();
    return apiClient<{ data: Bill[]; meta: PaginationMeta }>(
      `/bills${qs ? `?${qs}` : ''}`,
    );
  },

  /** gst-parked 120: the location's parked bills — every till at the store sees the same list. */
  getParkedBills(locationId: string) {
    return apiClient<ServerParkedBill[]>(`/bills/parked?location_id=${encodeURIComponent(locationId)}`);
  },

  /** T03 — the cashier's returns (a manager: their locations') with status. */
  listReturns(filters: ReturnListFilters = {}) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null) params.set(key, String(value));
    }
    const qs = params.toString();
    return apiClient<{ data: BillReturn[]; meta: PaginationMeta }>(`/bills/returns${qs ? `?${qs}` : ''}`);
  },

  /** T03 — withdraw a return still waiting for a manager. */
  cancelReturn(returnId: string) {
    return apiClient<{ id: string; return_number: string; status: BillReturnStatus }>(
      `/bills/returns/${returnId}/cancel`,
      { method: 'POST' },
    );
  },

  /**
   * Every bill the list filters match, as CSV (T20/T27) — all pages, under
   * the same scope as the list (a cashier: their own bills).
   */
  exportBills(filters: Omit<BillListFilters, 'page' | 'limit'>) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
    }
    const qs = params.toString();
    return apiClientBlob(`/bills/export${qs ? `?${qs}` : ''}`);
  },

  getReceipt(billId: string, format: '80mm' | '58mm' = '80mm') {
    const path = format === '58mm'
      ? `/bills/${billId}/receipt/58mm`
      : `/bills/${billId}/receipt`;
    return apiClientBlob(path);
  },

  getInvoice(billId: string) {
    return apiClientBlob(`/bills/${billId}/invoice`);
  },

  getCreditNote(billId: string) {
    return apiClientBlob(`/bills/${billId}/credit-note`);
  },

  /** `idempotencyKey`: one per return attempt, so a resent attempt is replayed, not repeated. */
  processReturn(billId: string, dto: ProcessReturnDto, idempotencyKey?: string) {
    // T12: a refund comes out of the drawer of the register paying it.
    const sessionId = getActiveRegisterSessionId(undefined);
    return apiClient<ProcessReturnResult>(`/bills/${billId}/return`, {
      method: 'POST',
      body: sessionId ? { ...dto, register_session_id: sessionId } : dto,
      ...(idempotencyKey ? { headers: { 'X-Idempotency-Key': idempotencyKey } } : {}),
    });
  },

  getAlternatives(billId: string) {
    return apiClient<{ product_id: string; alternatives: unknown[] }[]>(
      `/bills/${billId}/alternatives`,
    );
  },

  updateDraftBill(billId: string, dto: UpdateDraftBillDto) {
    return apiClient<BillWithItems>(`/bills/${billId}`, {
      method: 'PATCH',
      body: dto,
    });
  },

  priceOverride(billId: string, itemId: string, newPrice: number) {
    return apiClient<BillWithItems>(`/bills/${billId}/price-override`, {
      method: 'POST',
      body: { item_id: itemId, unit_price: newPrice },
    });
  },
};
